import React, { useState, useEffect } from 'react';
import { Loader, Button, Icon } from '@scuf/common';
import './HierarchyFileDrop.scss';

import ViewData from '../../commonUtils/ViewData';

import { ExcelAnalysisDetailsModal } from './ExcelAnalysisDetailsModal';
import useFetch from '../../commonUtils/useFetch';
import { IAPIPackage, consumeAPIs } from '../../commonUtils/APIContext';
import TreeNode from '../../commonUtils/BMSPPrimereactFork/treenode/TreeNode';
import { IExpectedAnalysisHierarchy } from '../onboardingModels';
import i18next from '../../../i18next';

export interface IHierarchyFileDropProps extends IAPIPackage {
  setUploadDocumentId: (documentId: string | undefined) => void;

  /**
   *  Become an PUT/PATCH instead of a POST
   */
  editOrgId?: string;

  /**
   * Pass classes to top level element
   */
  className?: string;
}

export interface IUploadReport {
  organisationName: string;
  pointOfContactName: string;
  pointOfContactEmail: string;
  pointOfContactTelephone: string;
  pointOfContactAddress: string;
  hierarchyData: IUploadReportHierarchyMetric;
  uploadTimestamp: number;
  analysisHierarchy: IExpectedAnalysisHierarchy[];
}

export interface IUploadReportHierarchyMetric {
  nodes: number;
  sites: number;
}

/**
 * Component for uploading (creating new) organisation hierarchies to the SMB system
 */
export const HierarchyFileDrop = (props: IHierarchyFileDropProps) => {
  const [currentFile, setCurrentFile] = useState<File | null>(null);

  const [
    uploadReportData,
    isLoadingUploadReport,
    errorUploadReport,
    fetchUploadReportData
  ] = useFetch(
    `${props.baseURL}/api/v1/organisations/${props.offering}/batch/upload${
      props.editOrgId ? '?organisationId=' + props.editOrgId : ''
    }`,
    props.authFetch,
    undefined,
    true
  );

  const [isDetailsScreenOpen, setIsDetailsScreenOpen] = useState<boolean>(
    false
  );

  const handleDropFile = (dragEvent: React.DragEvent<HTMLDivElement>) => {
    dragEvent.preventDefault();
    const file = dragEvent.dataTransfer.items[0].getAsFile();
    if (file) {
      setCurrentFile(file);
    }
  };

  const handleBrowseFileSelect = (
    changeEvent: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = changeEvent.currentTarget.files;
    if (files) {
      setCurrentFile(files[0]);
    }
  };

  const formatAddressString = (addressString: string) => {
    // Replaces all commas with newlines
    return addressString.replace(/,\s/g, '\n');
  };

  const sendNewUpload = () => {
    props.setUploadDocumentId(undefined);
    if (currentFile) {
      const postForm = new FormData();
      postForm.append('document', currentFile);
      const newOrgPostObject = {
        method: 'POST',
        body: postForm
      };
      fetchUploadReportData(newOrgPostObject);
    }
  };

  useEffect(() => {
    if (uploadReportData) {
      props.setUploadDocumentId(uploadReportData.documentId);
    }
  }, [uploadReportData]);

  useEffect(() => {
    if (currentFile) {
      sendNewUpload();
    }
  }, [currentFile]);

  const generateAnalysisHierarchy = (
    hierarchy: IExpectedAnalysisHierarchy[]
  ) => {
    let nodeHierarchy: TreeNode[] = convertAnalysisResponseToTreeNodeStructure(
      hierarchy
    );

    interface changeType {
      name: string;
      type: string;
      path: string[];
    }

    const shiftPath = (deletion: changeType): changeType => {
      const deletionShifted = {
        name: deletion.name,
        type: deletion.type,
        path: [...deletion.path]
      };
      deletionShifted.path.shift();
      return deletionShifted;
    };

    const injectRemovedNode = (
      hierarchy: TreeNode[],
      deletion: changeType
    ): TreeNode[] => {
      const matchingNode = hierarchy.find(
        node => node.data.name === deletion.path[0]
      );
      if (matchingNode) {
        // Deep copy so it doesnt directly edit the original object.
        const editedNode: TreeNode = JSON.parse(JSON.stringify(matchingNode));
        editedNode.children = injectRemovedNode(
          matchingNode.children,
          shiftPath(deletion)
        );
        return [...hierarchy.filter(node => node !== matchingNode), editedNode];
      } else {
        const newNode = generateRemovedHierarchy(deletion);
        return [...hierarchy.filter(node => node.key !== newNode.key), newNode];
      }
    };

    const generateRemovedHierarchy = (deletion: changeType): TreeNode => {
      const currentLevel = deletion.path[0];
      const remainingDeletion = shiftPath(deletion);

      return {
        key: '[REMOVED] ' + currentLevel,
        data: {
          name: '[REMOVED] ' + currentLevel,
          changes: {
            deletes: 1,
            creates: 0,
            updates: 0
          },
          type: deletion.name === currentLevel ? deletion.type : '[REMOVED]',
          address: ''
        },
        children:
          deletion.name === currentLevel || deletion.path.length <= 0
            ? []
            : [generateRemovedHierarchy(remainingDeletion)]
      };
    };

    uploadReportData.changes.deletes.forEach((deletion: changeType) => {
      nodeHierarchy = injectRemovedNode(nodeHierarchy, shiftPath(deletion));
    });

    return nodeHierarchy;
  };

  const convertAnalysisResponseToTreeNodeStructure = (
    hierarchy: IExpectedAnalysisHierarchy[]
  ) => {
    return hierarchy.map((node, index) => {
      const creates = uploadReportData.changes.creates.reduce(
        (n: number, creation: any) => {
          return n + +(creation.name === node.name);
        },
        0
      );

      const updates = uploadReportData.changes.updates.reduce(
        (n: number, update: any) => {
          return n + +(update.name === node.name);
        },
        0
      );

      const deletes = uploadReportData.changes.deletes.reduce(
        (n: number, deletion: any) => {
          return n + +(deletion.name === node.name);
        },
        0
      );
      const newNodeFormat: TreeNode = {
        key: node.name + 'key' + index,
        data: {
          changes: {
            creates: creates,
            updates: updates,
            deletes: deletes
          },
          name: node.name,
          address: node.address,
          type: node.type
        },
        children: convertAnalysisResponseToTreeNodeStructure(node.children)
      };
      return newNodeFormat;
    });
  };

  const closeDetailsScreen = () => {
    setIsDetailsScreenOpen(false);
  };

  return (
    <div className="hierarchy-file-drop">
      <div
        className={(props.className ? props.className : '') + ' drop-section'}
        onDragOver={e => {
          // Required to block the default behaviour that prevents drop events
          e.preventDefault();
        }}
        onDrop={handleDropFile}
      >
        <div>
          {i18next.t('dropExcel')}{' '}
          {/* This is a hidden file upload box, as to be able to upload/browse via clicking the label */}
          <input
            type="file"
            id="browseFile"
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleBrowseFileSelect(e);
            }}
            value={undefined}
          />
          <label className="browse-file-label" htmlFor="browseFile">
            {i18next.t('browse')}
          </label>
        </div>
        <>
          <ViewData
            isLoading={isLoadingUploadReport}
            isError={errorUploadReport}
            errorRender={() => (
              <div className="error-wrapper">
                <div className="error-report">
                  <div className="error-icon-wrapper">
                    <Icon name="badge-warning" size="large" />
                  </div>
                  <div className="error-text">
                    {errorUploadReport ? (
                      <>
                        {errorUploadReport.errorJson ? (
                          <>
                            <div>{errorUploadReport.errorJson.detail}</div>
                            <ul className="error-list">
                              {errorUploadReport.errorJson.errors &&
                                errorUploadReport.errorJson.errors[
                                  ''
                                ].map((errorString: string) => (
                                  <li>{errorString}</li>
                                ))}
                            </ul>
                          </>
                        ) : (
                          <>
                            {errorUploadReport.errorText
                              ? errorUploadReport.errorText
                              : null}
                          </>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            )}
            loadingRender={() => (
              <div className="default-loader-wrapper full-height">
                <Loader
                  text={`${i18next.t('uploading')} ${currentFile ? currentFile.name : ''}...`}
                  overlayOpacity={0}
                />
              </div>
            )}
            contentRender={() => (
              <>
                {uploadReportData ? (
                  <>
                    <div className="status-report-container">
                      <h3 className="status-report-name">
                        {uploadReportData.organisationHierarchy &&
                          uploadReportData.organisationHierarchy.name}
                      </h3>
                      <div className="status-report-grid">
                        <div>
                          {uploadReportData.organisationHierarchy &&
                            uploadReportData.organisationHierarchy.telephone}
                        </div>
                        <div>
                          {uploadReportData.organisationHierarchy &&
                            uploadReportData.organisationHierarchy.contactEmail}
                        </div>
                        <div className="status-report-field-address">
                          {formatAddressString(
                            uploadReportData.organisationHierarchy &&
                              uploadReportData.organisationHierarchy.address
                          )}
                        </div>
                        <div className="status-report-field-hierarchy-metrics">
                          <div className="data-metric">
                            {uploadReportData.nodeCount}
                          </div>
                          <div>{i18next.t('nodes')}</div>

                          <div className="data-metric">
                            {uploadReportData.siteCount}
                          </div>
                          <div>{i18next.t('siteInstallation')}</div>

                          <div className="data-metric">
                            {uploadReportData.changes &&
                              uploadReportData.changes.creates &&
                              uploadReportData.changes.creates.length}
                          </div>
                          <div>{i18next.t('new')}</div>

                          <div className="data-metric">
                            {uploadReportData.changes &&
                              uploadReportData.changes.updates &&
                              uploadReportData.changes.updates.length}
                          </div>
                          <div>{i18next.t('updated')}</div>

                          <div className="data-metric">
                            {uploadReportData.changes &&
                              uploadReportData.changes.deletes &&
                              uploadReportData.changes.deletes.length}
                          </div>
                          <div>{i18next.t('disabled')}</div>
                        </div>
                      </div>
                      {isDetailsScreenOpen &&
                      uploadReportData.organisationHierarchy ? (
                        <ExcelAnalysisDetailsModal
                          isRepload={!!props.editOrgId}
                          adminEmail={
                            uploadReportData.organisationHierarchy.contactEmail
                          }
                          address={
                            uploadReportData.organisationHierarchy.address
                          }
                          organisationName={
                            uploadReportData.organisationHierarchy.name
                          }
                          analysisHierarchy={generateAnalysisHierarchy(
                            uploadReportData.organisationHierarchy.children
                          )}
                          onClose={closeDetailsScreen}
                        />
                      ) : null}
                    </div>
                    <div className="summary-actions">
                      <Button
                        type="secondary"
                        onClick={() => setIsDetailsScreenOpen(true)}
                      >
                        {i18next.t('preview')}
                      </Button>
                    </div>
                  </>
                ) : null}
              </>
            )}
          />
        </>
      </div>
    </div>
  );
};

export default consumeAPIs(HierarchyFileDrop);
